"use client";

import { Check, ListChecks, RotateCcw, X } from "lucide-react";
import { useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useDemo } from "@/lib/demo/DemoContext";
import ActivityFrame from "./ActivityFrame";

export interface ClozeBlank {
  answers: string[];
}

export interface MCQuestion {
  kind?: "single" | "multi" | "short" | "cloze";
  prompt: string;
  options?: string[];
  correct?: number | number[];
  explanation?: string;
  // Cloze: text carries [[n]] markers in document order
  text?: string;
  // Present only in the instructor (editor) payload; stripped for students
  model_answer?: string;
  key_points?: string[];
  blanks?: ClozeBlank[];
}

// "error" = the grading call failed (not a score) — styled neutrally
type Verdict = "correct" | "partial" | "incorrect" | "error";
interface Result {
  verdict: Verdict;
  feedback?: string;
  blanks?: boolean[]; // cloze: per-blank correctness, in document order
}

const LETTERS = ["A", "B", "C", "D", "E", "F"];

function kindOf(q: MCQuestion): "single" | "multi" | "short" | "cloze" {
  return q.kind ?? "single";
}

// Split cloze text into alternating literal segments and blank numbers:
// "a [[1]] b" -> ["a ", "1", " b"] (even indices literal, odd indices blanks).
function clozeSegments(text: string): string[] {
  return (text ?? "").split(/\[\[(\d+)\]\]/);
}

function clozeBlankCount(text: string): number {
  return Math.floor(clozeSegments(text).length / 2);
}

export default function KnowledgeCheck({
  courseId,
  interactiveId,
  pageId,
  title,
  questions,
}: {
  courseId: string;
  interactiveId: string;
  pageId?: string;
  title: string;
  questions: MCQuestion[];
}) {
  const demo = useDemo();
  // answers[i]: number (single) | number[] (multi) | string (short) |
  //             string[] (cloze, one per blank)
  const [answers, setAnswers] = useState<Record<number, number | number[] | string | string[]>>({});
  const [results, setResults] = useState<Record<number, Result>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const allAnswered = useMemo(
    () =>
      questions.every((q, i) => {
        const a = answers[i];
        const kind = kindOf(q);
        if (kind === "multi") return Array.isArray(a) && a.length > 0;
        if (kind === "short") return typeof a === "string" && a.trim().length > 0;
        if (kind === "cloze") {
          const n = clozeBlankCount(q.text ?? "");
          return Array.isArray(a) && a.length === n && (a as string[]).every((s) => s.trim().length > 0);
        }
        return typeof a === "number";
      }),
    [answers, questions],
  );

  if (questions.length === 0) return null;

  const reset = () => {
    setAnswers({});
    setResults({});
    setSubmitted(false);
  };

  // response = the chosen option index / indices — feeds the instructor's
  // aggregate answer-distribution analytics (never stored per-student)
  const recordAttempt = (
    questionIndex: number,
    verdict: Verdict,
    response?: number | number[],
  ) => {
    if (demo) {
      demo.markTask?.("activity");
      return; // landing demo: nothing leaves the browser
    }
    api(`/api/v1/courses/${courseId}/interactives/${interactiveId}/attempt`, {
      method: "POST",
      body: JSON.stringify({
        question_index: questionIndex,
        result: verdict,
        page_id: pageId,
        response,
      }),
    }).catch(() => {});
  };

  const submit = async () => {
    setSubmitting(true);
    const next: Record<number, Result> = {};
    const shortJobs: Promise<void>[] = [];

    questions.forEach((q, i) => {
      const kind = kindOf(q);
      if (kind === "short") {
        const answer = String(answers[i] ?? "");
        shortJobs.push(
          api<Result>(
            `/api/v1/courses/${courseId}/interactives/${interactiveId}/grade-short`,
            { method: "POST", body: JSON.stringify({ question_index: i, answer }) },
          )
            .then((r) => {
              next[i] = r;
            })
            .catch(() => {
              next[i] = { verdict: "error", feedback: "Couldn't grade this answer right now — submit again to retry." };
            }),
        );
      } else if (kind === "cloze") {
        const blankAnswers = (answers[i] as string[]) ?? [];
        shortJobs.push(
          api<Result>(
            `/api/v1/courses/${courseId}/interactives/${interactiveId}/grade-cloze`,
            { method: "POST", body: JSON.stringify({ question_index: i, answers: blankAnswers }) },
          )
            .then((r) => {
              next[i] = r;
            })
            .catch(() => {
              next[i] = { verdict: "error", feedback: "Couldn't grade this answer right now — submit again to retry." };
            }),
        );
      } else if (kind === "multi") {
        const picked = new Set((answers[i] as number[]) ?? []);
        const correct = new Set((q.correct as number[]) ?? []);
        const ok = picked.size === correct.size && [...picked].every((x) => correct.has(x));
        const verdict: Verdict = ok ? "correct" : "incorrect";
        next[i] = { verdict, feedback: q.explanation };
        recordAttempt(i, verdict, [...picked]);
      } else {
        const ok = answers[i] === q.correct;
        const verdict: Verdict = ok ? "correct" : "incorrect";
        next[i] = { verdict, feedback: q.explanation };
        recordAttempt(i, verdict, answers[i] as number | undefined);
      }
    });

    await Promise.all(shortJobs);
    setResults(next);
    setSubmitted(true);
    setSubmitting(false);
  };

  const score = submitted
    ? questions.reduce((s, _, i) => {
        const v = results[i]?.verdict;
        return s + (v === "correct" ? 1 : v === "partial" ? 0.5 : 0);
      }, 0)
    : 0;

  return (
    <ActivityFrame
      type="knowledge_check"
      kindLabel="Knowledge check"
      icon={ListChecks}
      title={title}
      meta={
        submitted
          ? `Score ${score % 1 === 0 ? score : score.toFixed(1)} / ${questions.length}`
          : `${questions.length} question${questions.length === 1 ? "" : "s"}`
      }
    >
      <ol className="space-y-4">
        {questions.map((q, i) => (
          <Question
            key={i}
            q={q}
            index={i}
            answer={answers[i]}
            result={submitted ? results[i] : undefined}
            locked={submitted}
            onChange={(val) => setAnswers((prev) => ({ ...prev, [i]: val }))}
          />
        ))}
      </ol>

      <div className="mt-4 flex items-center justify-between">
        {submitted ? (
          <button onClick={reset} className="activity__btn flex items-center gap-1.5">
            <RotateCcw className="h-3.5 w-3.5" /> Try again
          </button>
        ) : (
          <span className="text-xs text-faint">
            {allAnswered ? "Ready to submit" : "Answer every question to submit"}
          </span>
        )}
        {!submitted && (
          <button
            onClick={submit}
            disabled={!allAnswered || submitting}
            className="rounded-md px-4 py-1.5 text-sm font-medium text-white transition-opacity disabled:opacity-40"
            style={{ background: "var(--la)" }}
          >
            {submitting ? "Checking…" : "Submit"}
          </button>
        )}
      </div>
    </ActivityFrame>
  );
}

function Question({
  q,
  index,
  answer,
  result,
  locked,
  onChange,
}: {
  q: MCQuestion;
  index: number;
  answer: number | number[] | string | string[] | undefined;
  result?: Result;
  locked: boolean;
  onChange: (val: number | number[] | string | string[]) => void;
}) {
  const kind = kindOf(q);
  return (
    <li className="kc-question">
      <p className="kc-prompt">
        <span className="kc-number">{index + 1}</span>
        <span>
          {q.prompt}
          {kind === "multi" && (
            <span className="ml-1.5 text-xs font-normal text-faint">(select all that apply)</span>
          )}
        </span>
      </p>

      {kind === "short" ? (
        <div className="mt-2">
          <textarea
            value={(answer as string) ?? ""}
            disabled={locked}
            onChange={(e) => onChange(e.target.value)}
            rows={3}
            placeholder="Type your answer…"
            aria-label={q.prompt}
            className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm focus:border-[var(--la)] focus:outline-none disabled:opacity-70"
          />
        </div>
      ) : kind === "cloze" ? (
        <ClozeText
          text={q.text ?? ""}
          answer={(answer as string[]) ?? []}
          result={result}
          locked={locked}
          onChange={onChange}
        />
      ) : (
        <div className="mt-2 space-y-1.5">
          {(q.options ?? []).map((option, oi) => {
            const isMulti = kind === "multi";
            const selected = isMulti
              ? Array.isArray(answer) && (answer as number[]).includes(oi)
              : answer === oi;
            const correctSet = isMulti
              ? new Set((q.correct as number[]) ?? [])
              : new Set([q.correct as number]);
            const isCorrect = correctSet.has(oi);
            const state = !result
              ? selected
                ? "is-selected"
                : ""
              : isCorrect
                ? "is-correct"
                : selected
                  ? "is-wrong"
                  : "is-dim";

            const toggle = () => {
              if (locked) return;
              if (isMulti) {
                const cur = new Set((answer as number[]) ?? []);
                if (cur.has(oi)) cur.delete(oi);
                else cur.add(oi);
                onChange([...cur]);
              } else {
                onChange(oi);
              }
            };

            return (
              <button
                key={oi}
                disabled={locked}
                onClick={toggle}
                aria-pressed={selected}
                className={`kc-option ${state}`}
              >
                <span className={`kc-marker ${isMulti ? "kc-marker--box" : ""} ${selected ? "is-on" : ""}`}>
                  {isMulti ? (selected ? <Check className="h-3 w-3" /> : null) : LETTERS[oi]}
                </span>
                <span className="flex-1">{option}</span>
                {result && isCorrect && <Check className="h-4 w-4 shrink-0" />}
                {result && selected && !isCorrect && <X className="h-4 w-4 shrink-0" />}
              </button>
            );
          })}
        </div>
      )}

      {result && (
        <div
          role="status"
          className={`kc-feedback ${
            result.verdict === "correct"
              ? "is-right"
              : result.verdict === "partial"
                ? "is-partial"
                : result.verdict === "error"
                  ? ""
                  : "is-wrong"
          }`}
        >
          <strong>
            {result.verdict === "correct"
              ? "Correct."
              : result.verdict === "partial"
                ? "Partially correct."
                : result.verdict === "error"
                  ? "Grading unavailable."
                  : "Not quite."}
          </strong>
          {result.feedback ? ` ${result.feedback}` : ""}
        </div>
      )}
    </li>
  );
}

// Renders cloze text inline with an input box per [[n]] marker. After grading,
// each box is tinted by its per-blank correctness from result.blanks.
function ClozeText({
  text,
  answer,
  result,
  locked,
  onChange,
}: {
  text: string;
  answer: string[];
  result?: Result;
  locked: boolean;
  onChange: (val: string[]) => void;
}) {
  const segments = clozeSegments(text);
  let blank = -1;
  return (
    <p className="kc-cloze mt-2 text-sm leading-8">
      {segments.map((seg, si) => {
        if (si % 2 === 0) return <span key={si}>{seg}</span>;
        blank += 1;
        const bi = blank;
        const state = result?.blanks
          ? result.blanks[bi]
            ? "border-ok text-ok"
            : "border-danger text-danger"
          : "border-line";
        return (
          <input
            key={si}
            type="text"
            value={answer[bi] ?? ""}
            disabled={locked}
            aria-label={`Blank ${bi + 1}`}
            onChange={(e) => {
              const next = [...answer];
              next[bi] = e.target.value;
              onChange(next);
            }}
            className={`mx-1 inline-block w-28 rounded border bg-surface px-2 py-0.5 text-sm focus:border-[var(--la)] focus:outline-none disabled:opacity-80 ${state}`}
          />
        );
      })}
    </p>
  );
}
